import baseStyles from "../..//toolbox.module.css";
import { useEffect, useState, useCallback } from "react";
import styles from "./toolbox-route-source.module.css";
import { Toolbox, BasicToolboxProps } from "../../toolbox";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { loadGPXFromFileInputEvent } from "PosterEditor/utils/file-import";
import { ToolboxDeleteButton } from "../../toolbox-components/toolbox-component-delete-button";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
import { GPSUploadSection } from "../asset-import/gps/gps-upload-section";
import { ToolboxSectionDivider as Divider } from "../../toolbox-components/toolbox-component-section";

interface ToolboxRouteSourceProps extends BasicToolboxProps {}

export const ToolboxRouteSource = (props: ToolboxRouteSourceProps) => {
  const { project, gpsSource } = usePosterProject();

  const getFirstUploadedFile = useCallback(() => {
    const first = gpsSource?.name;
    if (first) {
      return {
        name: first,
        numberOfPoints: gpsSource?.allCoordinates.length ?? 0,
      };
    }
  }, [gpsSource?.name]);

  const firstUploadedFile = getFirstUploadedFile();

  return (
    <Toolbox
      title="GPS Source"
      key={firstUploadedFile ? "uploaded" : "import"}
      collapsible={props.collapsible}
      isCollapsed={props.isCollapsed}
      onToggleCollapse={props.onToggleCollapse}
    >
      <div key={firstUploadedFile ? "uploaded" : "import"}>
        {firstUploadedFile ? (
          <UploadedFileList file={firstUploadedFile} />
        ) : (
          <CoordinateImport />
        )}
      </div>
    </Toolbox>
  );
};

const CoordinateImport = () => {
  const { project, setCoordinates, gpsSource } = usePosterProject();
  const [GPXUploadFound, setGPXUploadFound] = useState(false);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const data = await loadGPXFromFileInputEvent(event);
    let name = data?.name ?? event.target.files?.[0].name ?? "No file name";
    if (data?.coordinates) {
      setCoordinates(data.coordinates, name, data.data);
    }
  };

  const sourceFileName = gpsSource?.name ?? " ";

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <GPSUploadSection
          onFoundGPX={(found) => setGPXUploadFound(found)}
          emptyTitle="UPLOAD FROM OUR iPHONE APP"
          emptyDescription="Use our iOS app to upload an activity route, or make one out of photo
        locations"
        />
        {!GPXUploadFound && (
          <CTAButton
            title="Get the iOS app"
            variant="LINK"
            onClick={() => {}}
          />
        )}
      </div>
      <div className={styles.section}>
        <h1>UPLOAD .GPX FILE</h1>

        <FileUploadButton handleFileUpload={handleFileUpload} />

        {/* <img
          className={styles.gpxIcon}
          src="/pallete-components/GPX-ICON.svg"
          alt="GPX file icon"
        /> */}
        <div className={styles.gpxExplainerTextContainer}>
          <p>
            GPX files contain coordinates of your activities, and can easily be
            found on <a href="https://strava.com">Strava.com</a>
          </p>
        </div>

        <a className={styles.gifContainer} href="https://strava.com">
          <img
            className={styles.importFromStravaGif}
            src="/hint-gifs/gpx-from-strava.gif"
            alt="GPX file from Strava"
          />
        </a>
      </div>
    </div>
  );
};

export const FileUploadButton = ({
  handleFileUpload,
}: {
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  return (
    <div className={styles.uploadButtonWrapper}>
      <input
        id="file-upload"
        className={styles.uploadButton}
        type="file"
        accept=".gpx"
        onChange={handleFileUpload}
      />
      <label htmlFor="file-upload" className={styles.uploadButtonLabel}>
        SELECT A FILE
      </label>
    </div>
  );
};

interface UploadedFile {
  name: string;
  numberOfPoints: number;
}

interface UploadedFileListProps {
  file: UploadedFile;
}

const UploadedFileList = (props: UploadedFileListProps) => {
  const { deleteAllCoordinates } = usePosterProject();

  const numberOfPointsText =
    props.file.numberOfPoints === 1
      ? "1 point"
      : `${props.file.numberOfPoints} points`;

  const onClickDelete = () => {
    deleteAllCoordinates();
  };

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <div className={styles.uploadedFileContainer}>
          <div className={styles.uploadFileContainerTextContainer}>
            <span className={styles.uploadedFileTitle}>{props.file.name}</span>
            <span className={styles.uploadedFileSubtitle}>
              {numberOfPointsText}
            </span>
          </div>
          <ToolboxDeleteButton onclick={onClickDelete} />
        </div>
      </div>
    </div>
  );
};
